import { useI18n } from 'vue-i18n'

export function useDate() {
	const { t, locale } = useI18n();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const supportedLocales = ['en', 'ko', 'zh', 'vi'] as const;

	// 날짜 포맷팅 함수 (yyyy.mm.dd 형식)
	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('ko-KR', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		}).replace(/\./g, '.').replace(/\.$/g, '');
	};

	// 날짜 변환 함수(yyyy-mm-dd hh:mm:ss)
	const formatDateTime = (datetime: string) => {
		if (!datetime) return '-';
		const date = new Date(datetime);
		return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} 
		${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
	};

	/**
	 * 단위별 문자열 헬퍼
	 */
	const strYearLabel = (val: number) => t('common.timeLabel.year', { year: val })
	const strMonthLabel = (val: number) => t('common.timeLabel.month', { month: val })
	const strDayLabel = (val: number) => t('common.timeLabel.day', { day: val })
	const strHourLabel = (val: number) => t('common.hourUnit', { hour: val })
	const strMinuteLabel = (val: number) => t('common.minuteUnit', { minute: val })
	const strSecondLabel = (val: number) => t('common.timeLabel.second', { second: val })

	/**
	 * 초 단위를 기준으로 년, 월, 일, 시간, 분, 초로 변환
	 */
	const formatTime = (totalSeconds: number) => {
		const sec = totalSeconds
		const secondsInMinute = 60
		const secondsInHour = 60 * secondsInMinute
		const secondsInDay = 24 * secondsInHour
		const secondsInMonth = 30 * secondsInDay
		const secondsInYear = 12 * secondsInMonth

		const years = Math.floor(sec / secondsInYear)
		const months = Math.floor((sec % secondsInYear) / secondsInMonth)
		const days = Math.floor((sec % secondsInMonth) / secondsInDay)
		const hours = Math.floor((sec % secondsInDay) / secondsInHour)
		const minutes = Math.floor((sec % secondsInHour) / secondsInMinute)
		const seconds = sec % secondsInMinute

		const parts: string[] = []
		if (years) parts.push(strYearLabel(years))
		if (months) parts.push(strMonthLabel(months))
		if (days) parts.push(strDayLabel(days))
		if (hours) parts.push(strHourLabel(hours))
		if (minutes) parts.push(strMinuteLabel(minutes))
		parts.push(strSecondLabel(seconds))

		return { years, months, days, hours, minutes, seconds, formatted: parts.join(' ') }
	}

	/**
	 * 분 단위의 값을 년, 월, 일, 시간, 분으로 변환
	 */
	const formatTimeFromMinutes = (totalMinutes: number) => {
		const minutes = totalMinutes
		const minutesInHour = 60
		const minutesInDay = 24 * minutesInHour
		const minutesInMonth = 30 * minutesInDay
		const minutesInYear = 12 * minutesInMonth

		const years = Math.floor(minutes / minutesInYear)
		const months = Math.floor((minutes % minutesInYear) / minutesInMonth)
		const days = Math.floor((minutes % minutesInMonth) / minutesInDay)
		const hours = Math.floor((minutes % minutesInDay) / minutesInHour)
		const mins = minutes % minutesInHour

		const parts: string[] = []
		if (years) parts.push(strYearLabel(years))
		if (months) parts.push(strMonthLabel(months))
		if (days) parts.push(strDayLabel(days))
		if (hours) parts.push(strHourLabel(hours))
		parts.push(strMinuteLabel(mins))

		return { years, months, days, hours, minutes: mins, formatted: parts.join(' ') }
	}

	/**
	 * 시간 단위의 값을 년, 월, 일, 시간으로 변환
	 */
	const formatTimeFromHours = (totalHours: number) => {
		const hours = totalHours
		const hoursInDay = 24
		const hoursInMonth = 30 * hoursInDay
		const hoursInYear = 12 * hoursInMonth

		const years = Math.floor(hours / hoursInYear)
		const months = Math.floor((hours % hoursInYear) / hoursInMonth)
		const days = Math.floor((hours % hoursInMonth) / hoursInDay)
		const hrs = hours % hoursInDay

		const parts: string[] = []
		if (years) parts.push(strYearLabel(years))
		if (months) parts.push(strMonthLabel(months))
		if (days) parts.push(strDayLabel(days))
		parts.push(strHourLabel(hrs))

		return { years, months, days, hours: hrs, formatted: parts.join(' ') }
	}

	// 선택한 기간에 따라 날짜를 그룹화하는 키를 생성
	function getAllGroupKeys(
		period: 'day' | 'week' | 'month' | 'year',
		endDate: Date
	): string[] {
		const keys: string[] = [];

		if (period === 'day') {
			for (let hour = 0; hour < 24; hour++) {
				const date = new Date(endDate);
				date.setHours(hour, 0, 0, 0);
				keys.push(formatGroupKey('day', date));
			}
		} else if (period === 'week') {
			for (let i = 6; i >= 0; i--) {
				const date = new Date(endDate);
				date.setDate(endDate.getDate() - i);
				keys.push(formatGroupKey('week', date));
			}
		} else if (period === 'month') {
			const startDate = new Date(endDate);
			startDate.setMonth(endDate.getMonth() - 1);
			startDate.setDate(startDate.getDate() + 1);

			const date = new Date(startDate);
			while (date <= endDate) {
				keys.push(formatGroupKey('month', date));
				date.setDate(date.getDate() + 1);
			}
		} else if (period === 'year') {
			const startDate = new Date(endDate);
			startDate.setFullYear(endDate.getFullYear() - 1);
			startDate.setMonth(startDate.getMonth() + 1);

			let year = startDate.getFullYear();
			let month = startDate.getMonth();
			const endYear = endDate.getFullYear();
			const endMonth = endDate.getMonth();

			while (year < endYear || (year === endYear && month <= endMonth)) {
				const date = new Date(year, month, 1);
				keys.push(formatGroupKey('year', date));
				month++;
				if (month > 11) {
					year++;
					month = 0;
				}
			}
		}
		return keys;
	}

	// 선택한 기간에 따라 날짜를 그룹화하는 키를 생성
	const formatGroupKey = (period: string, date: Date): string => {
		switch (period) {
			case 'day': {
				const hour = date.getHours().toString().padStart(2, '0');
				switch (locale.value as typeof supportedLocales[number]) {
					case 'en':
						// 예: "03h"
						return `${hour}h`;
					case 'zh':
						// 예: "03点"
						return `${hour}点`;
					case 'vi':
						// 예: "03h"
						return `${hour}h`;
					default: // 'ko'
						// 예: "03시"
						return `${hour}시`;
				}
			}
			case 'week':
			case 'month': {
				// 주간/월간 데이터는 월-일 형식으로 처리
				switch (locale.value as typeof supportedLocales[number]) {
					case 'en':
						// 예: "Mar 31"
						return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
					case 'zh':
						// 예: "3月31日" (기본 옵션에 year는 포함되지 않으므로 뒤에 '日' 추가)
						return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })
					case 'vi':
						// 예: "31/03" (두 자리 숫자 형식)
						return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
					default: // ko (한국어)
						// "3월 31일"
						return date.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
				}
			}
			case 'year': {
				// 연간 데이터는 년도와 월을 함께 표시
				const year = date.getFullYear()
				const month = date.getMonth() + 1

				switch (locale.value as typeof supportedLocales[number]) {
					case 'en': {
						// 예: "Mar 2025"
						const monthName = date.toLocaleString('en-US', { month: 'short' })
						return `${monthName} ${year}`
					}
					case 'zh':
						// 예: "2025年3月"
						return `${year}年${month}月`
					case 'vi':
						// 예: "Tháng 3, 2025"
						return `Tháng ${month}, ${year}`
					default: {
						// 한국어: "2025년 03월" (월은 두 자리로)
						const paddedMonth = month.toString().padStart(2, '0')
						return `${year}년 ${paddedMonth}월`
					}
				}
			}
			default:
				return ''
		}
	}

	return {
		formatDate,
		formatTime,
		formatTimeFromMinutes,
		formatTimeFromHours,
		formatDateTime,
		formatGroupKey,
		getAllGroupKeys,
		strYearLabel,
		strMonthLabel,
		strDayLabel,
		strHourLabel,
		strMinuteLabel,
		strSecondLabel,
	}
}